import { useEffect, useState, type ReactNode } from "react";
import {
  eInvoiceDeviations, eInvoiceFormatLabel, eInvoiceSelfCheck, eInvoiceSummary, type EInvoiceData, type EInvoiceExpected, type EInvoiceReadParty,
} from "@talentkontor/domain";
import { currentLang, t } from "../lib/sprache";
import { date, money } from "../lib/format";
import { readStoredEInvoice, type EInvoiceView } from "../model/e-rechnung";
import { Dialog } from "./ui";

/**
 * Steuerprüfung L12: Sichtansicht einer E-Rechnung für Menschen (XRechnung/EN 16931 als XML, ZUGFeRD/Factur-X). Ein Fenster
 * für beide Portale, geöffnet über zeigeERechnung (wie die Steuer-Rückfrage). Gelesen wird immer das Original aus dem
 * Speicher; die Prüfsumme zeigt, ob es seit dem Hochladen unverändert ist. Abweichungen zu den Angaben im Portal bzw. zur
 * Rechnungsanforderung stehen als Hinweis darüber, nie als Sperre.
 */
export interface ERechnungQuelle {
  title: string;
  fileName: string;
  laden: () => Promise<Blob>;
  /** beim Hochladen gespeicherte Prüfsumme der Datei */
  sha256?: string | null;
  /** Werte im Portal, mit denen verglichen wird */
  erwartet?: EInvoiceExpected | null;
}

let zeigen: ((q: ERechnungQuelle | null) => void) | null = null;

/** Sichtansicht öffnen; false, wenn kein Fenster eingebunden ist (dann lädt die Seite die Datei herunter) */
export function zeigeERechnung(q: ERechnungQuelle): boolean {
  if (!zeigen) return false;
  zeigen(q);
  return true;
}

export function ERechnungHost() {
  const [quelle, setQuelle] = useState<ERechnungQuelle | null>(null);
  const [stand, setStand] = useState<{ view: EInvoiceView; blob: Blob } | { fehler: string } | null>(null);
  useEffect(() => {
    zeigen = (q) => { setQuelle(q); setStand(null); };
    return () => { zeigen = null; };
  }, []);
  useEffect(() => {
    if (!quelle) return;
    let aktiv = true;
    void (async () => {
      try {
        const blob = await quelle.laden();
        const view = await readStoredEInvoice(blob, quelle.sha256, currentLang(), quelle.fileName);
        if (aktiv) setStand({ view, blob });
      } catch (e) {
        if (aktiv) setStand({ fehler: (e as Error).message || t("Die E-Rechnung ließ sich nicht lesen.", "The e-invoice could not be read.") });
      }
    })();
    return () => { aktiv = false; };
  }, [quelle]);
  if (!quelle) return null;
  return (
    <Dialog open onClose={() => setQuelle(null)} title={quelle.title} wide>
      {!stand && <p role="status">{t("E-Rechnung wird gelesen …", "Reading e-invoice …")}</p>}
      {stand && "fehler" in stand && <p className="tk-error" role="alert">{stand.fehler}</p>}
      {stand && "view" in stand && <ERechnungInhalt view={stand.view} erwartet={quelle.erwartet ?? null} original={{ blob: stand.blob, name: quelle.fileName }} />}
    </Dialog>
  );
}

function speichern(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const Zeile = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="row between"><span className="muted">{label}</span><span>{children}</span></div>
);

function Partei({ titel, p }: { titel: string; p: EInvoiceReadParty }) {
  const adresse = [p.street, [p.postalCode, p.city].filter(Boolean).join(" "), p.country].filter(Boolean).join(", ");
  return (
    <section className="panel stack tk-gap-sm" aria-label={titel}>
      <strong>{titel}</strong>
      <span>{p.name ?? "–"}</span>
      {adresse && <small>{adresse}</small>}
      {p.vatId && <small>{t("USt-IdNr.", "VAT ID")} {p.vatId}</small>}
      {p.taxNumber && <small>{t("Steuernummer", "Tax number")} {p.taxNumber}</small>}
      {p.email && <small>{p.email}</small>}
    </section>
  );
}

const prozent = (n: number | null) => (n === null ? "–" : `${currentLang() === "en" ? String(n) : String(n).replace(".", ",")} %`);
const tag = (d: string | null) => (d ? date(d) : "–");

/** Inhalt der Sichtansicht (eigenständig prüfbar) */
export function ERechnungInhalt({ view, erwartet, original }: { view: EInvoiceView; erwartet: EInvoiceExpected | null; original?: { blob: Blob; name: string } }) {
  const d: EInvoiceData = view.file.data;
  const lang = currentLang();
  const cur = d.currency ?? "EUR";
  const geld = (c: number | null) => (c === null ? "–" : money(c, cur));
  const format = eInvoiceFormatLabel(eInvoiceSummary(view.file, view.sha256));
  const abweichungen = erwartet ? eInvoiceDeviations(d, erwartet, lang) : [];
  const pruefung = eInvoiceSelfCheck(d, lang);
  const art = d.typeCode === "381" ? t("Rechnungskorrektur bzw. Gutschrift (381)", "Credit note or correction (381)")
    : d.typeCode === "389" ? t("Gutschrift im Gutschriftverfahren (389)", "Self-billed invoice (389)")
    : d.typeCode === "380" ? t("Rechnung (380)", "Invoice (380)") : d.typeCode ?? "–";
  return (
    <div className="stack tk-erechnung">
      <p>
        <strong>{format}</strong>
        {view.file.container === "pdf" && view.file.xmlName ? <> · {t(`eingebettet als ${view.file.xmlName}`, `embedded as ${view.file.xmlName}`)}</> : null}
      </p>
      {view.unchanged === true && <small className="muted">{t(`Original unverändert seit dem Hochladen (SHA-256 ${view.sha256.slice(0, 12)}…).`, `Original unchanged since upload (SHA-256 ${view.sha256.slice(0, 12)}…).`)}</small>}
      {view.unchanged === false && <p className="tk-error" role="alert">{t("Die Datei im Speicher stimmt nicht mit der Prüfsumme beim Hochladen überein.", "The stored file does not match the checksum recorded at upload.")}</p>}
      {abweichungen.length > 0 && (
        <div className="notice stack tk-gap-sm" role="status">
          <strong>{t("Abweichungen zu den Angaben im Portal", "Differences from the details in the portal")}</strong>
          <ul className="tk-findings">{abweichungen.map((f) => <li key={f.key}>{f.text}</li>)}</ul>
          <small>{t("Maßgeblich ist die E-Rechnung. Bitte klären, welche Angabe stimmt.", "The e-invoice is authoritative. Please clarify which detail is correct.")}</small>
        </div>
      )}
      {pruefung.length > 0 && (
        <div className="stack tk-gap-sm" role="status">
          <strong>{t("Hinweise zur E-Rechnung", "Notes on the e-invoice")}</strong>
          <ul className="tk-findings">{pruefung.map((f) => <li key={f.key}>{f.text}</li>)}</ul>
        </div>
      )}
      <div className="stack tk-gap-sm">
        <Zeile label={t("Rechnungsnummer", "Invoice number")}><strong>{d.number ?? "–"}</strong></Zeile>
        <Zeile label={t("Art", "Type")}>{art}</Zeile>
        <Zeile label={t("Rechnungsdatum", "Invoice date")}>{tag(d.issueDate)}</Zeile>
        <Zeile label={t("Leistungszeitraum", "Service period")}>{d.serviceFrom || d.serviceTo ? `${tag(d.serviceFrom)} – ${tag(d.serviceTo)}` : d.deliveryDate ? tag(d.deliveryDate) : "–"}</Zeile>
        {d.dueDate && <Zeile label={t("Fällig", "Due")}>{tag(d.dueDate)}</Zeile>}
        <Zeile label={t("Währung", "Currency")}>{d.currency ?? "–"}</Zeile>
        {d.buyerReference && <Zeile label={t("Käuferreferenz", "Buyer reference")}>{d.buyerReference}</Zeile>}
        {d.orderReference && <Zeile label={t("Bestellnummer", "Purchase order")}>{d.orderReference}</Zeile>}
        {d.precedingNumber && <Zeile label={t("Bezug auf Rechnung", "Refers to invoice")}>{d.precedingNumber}</Zeile>}
        {d.iban && <Zeile label="IBAN">{d.iban}</Zeile>}
        {d.paymentTerms && <Zeile label={t("Zahlungsbedingungen", "Payment terms")}>{d.paymentTerms}</Zeile>}
      </div>
      <div className="grid">
        <Partei titel={t("Aussteller", "Seller")} p={d.seller} />
        <Partei titel={t("Empfänger", "Buyer")} p={d.buyer} />
      </div>
      {d.lines.length > 0 && (
        <div className="tablewrap">
          <table>
            <caption className="sr-only">{t("Positionen", "Items")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("Position", "Item")}</th>
                <th scope="col">{t("Menge", "Qty")}</th>
                <th scope="col" className="money">{t("Einzelpreis", "Unit price")}</th>
                <th scope="col">{t("Steuersatz", "Tax rate")}</th>
                <th scope="col" className="money">{t("Summe netto", "Net total")}</th>
              </tr>
            </thead>
            <tbody>
              {d.lines.map((l, i) => (
                <tr key={`${l.id ?? ""}-${i}`}>
                  <td>{l.name ?? "–"}</td>
                  <td>{l.quantity ?? "–"}{l.unitCode && l.unitCode !== "C62" ? ` ${l.unitCode}` : ""}</td>
                  <td className="num">{geld(l.unitPriceCents)}</td>
                  <td>{prozent(l.ratePercent)}</td>
                  <td className="num">{geld(l.netCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d.taxes.length > 0 && (
        <div className="tablewrap">
          <table>
            <caption className="sr-only">{t("Steuer je Satz", "Tax per rate")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("Steuer je Satz", "Tax per rate")}</th>
                <th scope="col" className="money">{t("Netto", "Net")}</th>
                <th scope="col" className="money">{t("Steuer", "Tax")}</th>
              </tr>
            </thead>
            <tbody>
              {d.taxes.map((x, i) => (
                <tr key={i}>
                  <td>{prozent(x.ratePercent)}{x.category && x.category !== "S" ? ` (${x.category})` : ""}{x.exemptionReason && <small>{x.exemptionReason}</small>}</td>
                  <td className="num">{geld(x.basisCents)}</td>
                  <td className="num">{geld(x.taxCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="stack tk-gap-sm">
        <Zeile label={t("Netto", "Net")}><span className="num">{geld(d.totals.netCents)}</span></Zeile>
        <Zeile label={t("Umsatzsteuer", "VAT")}><span className="num">{geld(d.totals.taxCents)}</span></Zeile>
        <Zeile label={t("Brutto", "Gross")}><strong className="num">{geld(d.totals.grossCents)}</strong></Zeile>
        {d.totals.prepaidCents ? <Zeile label={t("Bereits gezahlt", "Already paid")}><span className="num">{geld(d.totals.prepaidCents)}</span></Zeile> : null}
        {d.totals.payableCents !== null && d.totals.payableCents !== d.totals.grossCents && <Zeile label={t("Zu zahlen", "Amount due")}><span className="num">{geld(d.totals.payableCents)}</span></Zeile>}
      </div>
      {d.notes.length > 0 && <div className="stack tk-gap-sm"><strong>{t("Hinweise im Beleg", "Notes in the document")}</strong>{d.notes.map((n, i) => <small key={i}>{n}</small>)}</div>}
      <div className="row">
        <button type="button" onClick={() => speichern(new Blob([view.file.xml as BlobPart], { type: "application/xml" }), view.file.xmlName && /\.xml$/i.test(view.file.xmlName) ? view.file.xmlName : `${(d.number ?? "e-rechnung").replace(/[^A-Za-z0-9._-]+/g, "-")}.xml`)}>
          {t("XML herunterladen", "Download XML")}
        </button>
        {original && view.file.container === "pdf" && <button type="button" onClick={() => speichern(original.blob, original.name)}>{t("PDF herunterladen", "Download PDF")}</button>}
      </div>
      <small className="muted">{t("Diese Ansicht liest das Original. Rechtlich maßgeblich ist das XML.", "This view reads the original. The XML is legally authoritative.")}</small>
    </div>
  );
}
